import * as React from "react"
import useForceUpdate from "@digits-shared/hooks/useForceUpdate"
import useSession from "@digits-shared/hooks/useSession"
import Session from "@digits-shared/session/Session"
import type FrontendSession from "src/frontend/session"
import { type EmployeeFeatureKey, employeeFeatureKeys } from "src/shared/config/employeeFeatures"

/**
 * Returns a memoized calculation of the employee feature.
 * NOTE: This value is memoized on the session, which is a stable reference,
 * so the value read by this hook will not update if the value changes.
 * This is an intentional move since the value is read from local storage.
 */
export function useReadEmployeeFeature(employeeFeatureKey: EmployeeFeatureKey) {
  const session = useSession<FrontendSession>()
  const [_, forceUpdate] = useForceUpdate()
  // Session changes
  React.useEffect(() => {
    session.on?.(Session.USER_PREFERENCES_CHANGE, forceUpdate)
    return () => {
      session.off?.(Session.USER_PREFERENCES_CHANGE, forceUpdate)
    }
  }, [forceUpdate, session])
  return React.useMemo(
    () => _ && getEmployeeFeature(session, employeeFeatureKey),
    [_, session, employeeFeatureKey]
  )
}

/**
 * Returns a convenience function that refuses to set anything but employeeFeatureKeys
 */
export function useSetEmployeeFeature(
  employeeFeatureKey: EmployeeFeatureKey,
  onSetFeature?: () => void
) {
  const session = useSession<FrontendSession>()
  const setEmployeeFeature = React.useCallback(
    (enabled?: boolean) => {
      const userPreferenceKey = employeeFeatureKeys[employeeFeatureKey]
      if (enabled === undefined) {
        session.deleteUserPreference(userPreferenceKey)
      } else {
        session.setUserPreference(userPreferenceKey, enabled)
      }
      onSetFeature?.()
    },
    [employeeFeatureKey, onSetFeature, session]
  )

  return setEmployeeFeature
}

/**
 * Returns a tuple including the current state value and a setter that forces the recalculation of the state
 */
export function useEmployeeFeatureState(employeeFeatureKey: EmployeeFeatureKey) {
  const session = useSession<FrontendSession>()
  const [updateKey, forceUpdate] = useForceUpdate()
  const setEmployeeFeature = useSetEmployeeFeature(employeeFeatureKey, forceUpdate)
  const employeeFeatureState = React.useMemo(
    () => getEmployeeFeature(session, employeeFeatureKey),
    // use the updateKey to force the recalculation of this value
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, employeeFeatureKey, updateKey]
  )

  return [employeeFeatureState, setEmployeeFeature] as const
}

function getEmployeeFeature(session: Session, employeeFeatureKey: EmployeeFeatureKey) {
  const userPreferenceKey = employeeFeatureKeys[employeeFeatureKey]
  return session.getBooleanUserPreference(userPreferenceKey)
}
